// MuseIQ - alphaTab enum values for code that can't read the global alphaTab
//
// Shared by the browser (dynamics judging, velocity meter) and the Node tests.
// Code under public/shared/ must not touch the DOM, window, the global alphaTab
// or Lit, and takes alphaTab types only from lib/alphaTab.d.ts (type-only, so
// nothing is loaded at runtime). tsconfig.shared.json checks this with no DOM lib.
//
// Each table is typed member-by-member against lib's enum, so a renamed,
// renumbered, added or removed member fails tsc instead of drifting at runtime.
// Browser code that already has the global can read alphaTab.model.* instead.

import type { model } from '../../lib/alphaTab';

/** A value table checked against an alphaTab enum: every member, same value. */
type EnumTable<E> = { readonly [K in keyof E]: E[K] };

/**
 * Dynamic marking of a beat. Values 0-7 are the ordinary graded levels; 8-13
 * are extended levels; 14-25 are accent/composite marks that are NOT points on
 * the loudness scale (see dynamics-ranges.ts for how they're handled).
 */
export type DynamicValue = model.DynamicValue;
export const DynamicValue: EnumTable<typeof model.DynamicValue> = {
    PPP: 0,
    PP: 1,
    P: 2,
    MP: 3,
    MF: 4,
    F: 5,
    FF: 6,
    FFF: 7,

    // extended levels
    PPPP: 8,
    PPPPP: 9,
    PPPPPP: 10,
    FFFF: 11,
    FFFFF: 12,
    FFFFFF: 13,

    // accent / composite marks
    SF: 14,
    SFP: 15,
    SFPP: 16,
    FP: 17,
    RF: 18,
    RFZ: 19,
    SFZ: 20,
    SFFZ: 21,
    FZ: 22,
    N: 23,
    PF: 24,
    SFZP: 25,
};

/**
 * Crescendo/decrescendo state of a beat. alphaTab's MusicXML importer sets this
 * from <wedge> directions and holds it from wedge start until wedge stop, but it
 * does NOT interpolate beat.dynamics across the span.
 */
export type CrescendoType = model.CrescendoType;
export const CrescendoType: EnumTable<typeof model.CrescendoType> = {
    None: 0,
    Crescendo: 1,
    Decrescendo: 2,
};
